// src/pages/projectDetailTabs/ExpensesTab.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useData } from "../../DataContext";
import { generateId } from "../../utils/id";
import { apiRequest } from "../../api/http";

import { Card } from "../../components/ui/Card";
import { TableShell, CellClamp } from "../../components/ui/TableShell";

import { projectDisplayLabelPretty, subContractDisplayLabelPretty } from "../../utils/format";

type Props = {
  projectId: string;
  onChanged?: () => void | Promise<void>;
};

type ExpenseType = "Expense" | "Sub-Contract Payment";
type PaymentMethod = "Cash" | "Bank";
type TransferDirection = "out" | "in";

type ApiTransfer = {
  id: string;
  source_project_id: string;
  destination_project_id: string;
  source_project_name?: string;
  destination_project_name?: string;
  amount: number;
  payment_method: PaymentMethod;
  date: string;
  notes?: string | null;
};

type ProjectBalances = Record<string, { Cash: number; Bank: number }>;

// --- DB row shape (expenses table) ---
type ApiExpense = {
  id: string;
  tenant_id: string;
  project_id: string;

  expense_type: ExpenseType;
  who_paid: string;
  amount: number;
  payment_method: PaymentMethod;
  date: string; // YYYY-MM-DD
  notes: string | null;
  sub_contract_id: string | null;

  created_at?: string;
  updated_at?: string;
};

// --- UI shape ---
type UiExpense = {
  id: string;
  projectId: string;

  expenseType: ExpenseType;
  whoPaid: string;
  amount: number;
  paymentMethod: PaymentMethod;
  date: string;
  notes?: string;
  subContractId?: string | null;
};

type ApiOk<T> = { ok: true; data: T };
type ApiErr = { ok: false; error: string };

const money = (n: number) =>
  `$${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function toUi(row: ApiExpense): UiExpense {
  return {
    id: row.id,
    projectId: row.project_id,
    expenseType: row.expense_type,
    whoPaid: row.who_paid,
    amount: Number(row.amount) || 0,
    paymentMethod: row.payment_method,
    date: row.date,
    notes: row.notes || undefined,
    subContractId: row.sub_contract_id ?? null,
  };
}

function uniqStrings(arr: string[]) {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const s of arr) {
    const v = (s || "").trim();
    if (!v) continue;
    const k = v.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(v);
  }
  return out;
}

export default function ExpensesTab({ projectId, onChanged }: Props) {
  const { t } = useTranslation();
  const {
    tenantId,
    subContracts,
    partners,
    reloadExpensesFromApi,

    // for pretty subcontract label (same dependency set you used elsewhere)
    projects,
    clients,
    resources,

    // keep backward compat if you still have this in DataContext
    subContractors,
  } = useData() as any;

  const [items, setItems] = useState<UiExpense[]>([]);
  const [transfers, setTransfers] = useState<ApiTransfer[]>([]);
  const [projectBalances, setProjectBalances] = useState<ProjectBalances>({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Add form
  const [expenseType, setExpenseType] = useState<ExpenseType>("Expense");
  const [whoPaid, setWhoPaid] = useState("Company");
  const [whoPaidMode, setWhoPaidMode] = useState<"select" | "custom">("select");
  const [whoPaidCustom, setWhoPaidCustom] = useState("");

  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("Cash");
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [subContractId, setSubContractId] = useState<string>("");

  // Inter-project transfer form
  const [transferDirection, setTransferDirection] = useState<TransferDirection>("out");
  const [transferProjectId, setTransferProjectId] = useState("");
  const [transferAmount, setTransferAmount] = useState("");
  const [transferMethod, setTransferMethod] = useState<PaymentMethod>("Cash");
  const [transferDate, setTransferDate] = useState("");
  const [transferNotes, setTransferNotes] = useState("");
  const [transferSaving, setTransferSaving] = useState(false);
  const [editingTransferId, setEditingTransferId] = useState<string | null>(null);
  const appliedTransferPrefill = useRef(false);

  useEffect(() => {
    if (appliedTransferPrefill.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("transfer") !== "in") return;
    appliedTransferPrefill.current = true;
    setTransferDirection("in");
    const methodParam = params.get("method");
    if (methodParam === "Cash" || methodParam === "Bank") setTransferMethod(methodParam);
    const amountParam = Number(params.get("amount"));
    if (Number.isFinite(amountParam) && amountParam > 0) setTransferAmount(amountParam.toFixed(2));
  }, []);

  // Inline edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editExpenseType, setEditExpenseType] = useState<ExpenseType>("Expense");
  const [editWhoPaid, setEditWhoPaid] = useState("Company");
  const [editWhoPaidMode, setEditWhoPaidMode] = useState<"select" | "custom">("select");
  const [editWhoPaidCustom, setEditWhoPaidCustom] = useState("");

  const [editAmount, setEditAmount] = useState("");
  const [editPaymentMethod, setEditPaymentMethod] = useState<PaymentMethod>("Cash");
  const [editDate, setEditDate] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editSubContractId, setEditSubContractId] = useState<string>("");

  const payerOptions = useMemo(() => {
    const partnerNames = Array.isArray(partners)
      ? partners.map((p: any) => (p?.name || "").toString().trim()).filter(Boolean)
      : [];
    return ["Company", ...uniqStrings(partnerNames)];
  }, [partners]);

  // ✅ subcontractors list (prefer resources → fallback to subContractors)
  const dbSubcontractors = useMemo(() => {
    const fromResources = (resources || [])
      .filter((r: any) => Number(r.isSubcontractor ?? r.is_subcontractor ?? 0) === 1)
      .map((r: any) => ({ id: r.id, name: r.name }));

    if (fromResources.length) return fromResources;

    // fallback
    return (subContractors || []).map((s: any) => ({ id: s.id, name: s.name }));
  }, [resources, subContractors]);

  // ✅ Pretty subcontract label using existing util (no rebuild)
  const subContractPretty = (id: string) => {
    const sc = (subContracts || []).find((x: any) => x.id === id);
    if (!sc) return id || "—";
    return subContractDisplayLabelPretty(sc, projects || [], clients || [], dbSubcontractors || []);
  };

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const [res, transferRes] = await Promise.all([
        apiRequest<ApiOk<ApiExpense[]> | ApiErr>(
          "GET",
          `/api/expenses?project_id=${encodeURIComponent(projectId)}`,
          { tenantId }
        ),
        apiRequest<any>(
          "GET",
          `/api/project_transfers?project_id=${encodeURIComponent(projectId)}`,
          { tenantId }
        ).catch((error) => {
          console.warn("Project transfers are temporarily unavailable:", error);
          return { ok: false, data: [], balances: {} };
        }),
      ]);

      if ((res as any).ok) {
        const rows = ((res as any).data || []) as ApiExpense[];
        setItems(rows.map(toUi));
        setTransfers((transferRes?.data || []) as ApiTransfer[]);
        setProjectBalances((transferRes?.balances || {}) as ProjectBalances);
      } else {
        setErr((res as any).error || t("expenses.tab.loadError"));
      }
    } catch (e: any) {
      setErr(e?.message || t("expenses.tab.loadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, projectId]);

  const totals = useMemo(() => {
    const total = items.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    const cash = items
      .filter((e) => e.paymentMethod === "Cash")
      .reduce((s, e) => s + (Number(e.amount) || 0), 0);
    const bank = items
      .filter((e) => e.paymentMethod === "Bank")
      .reduce((s, e) => s + (Number(e.amount) || 0), 0);

    const subcontractPayments = items
      .filter((e) => e.expenseType === "Sub-Contract Payment")
      .reduce((s, e) => s + (Number(e.amount) || 0), 0);

    return { total, cash, bank, subcontractPayments };
  }, [items]);

  const resolvedWhoPaid = () => {
    if (whoPaidMode === "custom") return (whoPaidCustom || "").trim();
    return (whoPaid || "").trim();
  };

  const resolvedEditWhoPaid = () => {
    if (editWhoPaidMode === "custom") return (editWhoPaidCustom || "").trim();
    return (editWhoPaid || "").trim();
  };

  const handleAdd = async () => {
    const payer = resolvedWhoPaid();

    if (!payer || !amount || !date) {
      alert(t("expenses.tab.whoPaidAmountDateRequired"));
      return;
    }

    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      alert(t("expenses.amountInvalid"));
      return;
    }

    if (expenseType === "Sub-Contract Payment" && !subContractId) {
      alert(t("expenses.subRequired"));
      return;
    }

    const newId = generateId("EXP");

    try {
      const res = await apiRequest<ApiOk<ApiExpense> | ApiErr>("POST", "/api/expenses", {
        tenantId,
        body: {
          id: newId,
          project_id: projectId,
          expense_type: expenseType,
          who_paid: payer,
          amount: amt,
          payment_method: paymentMethod,
          date,
          notes: notes.trim() || null,
          sub_contract_id: expenseType === "Sub-Contract Payment" ? subContractId : null,
        },
      });

      if (!(res as any).ok) {
        alert((res as any).error || t("expenses.tab.addError"));
        return;
      }

      const created = toUi((res as any).data as ApiExpense);
      setItems((prev) => [created, ...prev]);

      await reloadExpensesFromApi?.();
      await onChanged?.();

      // reset form
      setExpenseType("Expense");
      setWhoPaid("Company");
      setWhoPaidMode("select");
      setWhoPaidCustom("");
      setAmount("");
      setPaymentMethod("Cash");
      setDate("");
      setNotes("");
      setSubContractId("");
    } catch (e: any) {
      alert(e?.message || t("expenses.tab.addError"));
    }
  };

  const startEdit = (e: UiExpense) => {
    setEditingId(e.id);
    setEditExpenseType(e.expenseType);

    const payer = (e.whoPaid || "Company").trim();
    if (payerOptions.some((x) => x.toLowerCase() === payer.toLowerCase())) {
      const normalized =
        payerOptions.find((x) => x.toLowerCase() === payer.toLowerCase()) || "Company";
      setEditWhoPaid(normalized);
      setEditWhoPaidMode("select");
      setEditWhoPaidCustom("");
    } else {
      setEditWhoPaid("Company");
      setEditWhoPaidMode("custom");
      setEditWhoPaidCustom(payer);
    }

    setEditAmount(String(e.amount ?? ""));
    setEditPaymentMethod(e.paymentMethod);
    setEditDate(e.date || "");
    setEditNotes(e.notes || "");
    setEditSubContractId(e.subContractId ? String(e.subContractId) : "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditExpenseType("Expense");
    setEditWhoPaid("Company");
    setEditWhoPaidMode("select");
    setEditWhoPaidCustom("");
    setEditAmount("");
    setEditPaymentMethod("Cash");
    setEditDate("");
    setEditNotes("");
    setEditSubContractId("");
  };

  const saveEdit = async (e: UiExpense) => {
    const payer = resolvedEditWhoPaid();

    if (!payer || !editAmount || !editDate) {
      alert(t("expenses.tab.whoPaidAmountDateRequired"));
      return;
    }

    const amt = Number(editAmount);
    if (!Number.isFinite(amt) || amt <= 0) {
      alert(t("expenses.amountInvalid"));
      return;
    }

    if (editExpenseType === "Sub-Contract Payment" && !editSubContractId) {
      alert(t("expenses.subRequired"));
      return;
    }

    try {
      const res = await apiRequest<ApiOk<ApiExpense> | ApiErr>(
        "PATCH",
        `/api/expenses/${encodeURIComponent(e.id)}`,
        {
          tenantId,
          body: {
            expense_type: editExpenseType,
            who_paid: payer,
            amount: amt,
            payment_method: editPaymentMethod,
            date: editDate,
            notes: editNotes.trim() || null,
            sub_contract_id: editExpenseType === "Sub-Contract Payment" ? editSubContractId : null,
          },
        }
      );

      if (!(res as any).ok) {
        alert((res as any).error || t("expenses.tab.updateError"));
        return;
      }

      const updated = toUi((res as any).data as ApiExpense);
      setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));

      await reloadExpensesFromApi?.();
      await onChanged?.();

      cancelEdit();
    } catch (e2: any) {
      alert(e2?.message || t("expenses.tab.updateError"));
    }
  };

  const doDelete = async (e: UiExpense) => {
    const ok = window.confirm(
      `Delete this expense?\n\n${e.expenseType}\n${money(e.amount)} • ${e.paymentMethod}\n${e.notes || ""}`
    );
    if (!ok) return;

    try {
      const res = await apiRequest<ApiOk<any> | ApiErr>(
        "DELETE",
        `/api/expenses/${encodeURIComponent(e.id)}`,
        { tenantId }
      );

      if (!(res as any).ok) {
        alert((res as any).error || t("expenses.tab.deleteError"));
        return;
      }

      setItems((prev) => prev.filter((x) => x.id !== e.id));

      await reloadExpensesFromApi?.();
      await onChanged?.();

      if (editingId === e.id) cancelEdit();
    } catch (err2: any) {
      alert(err2?.message || t("expenses.tab.deleteError"));
    }
  };

  const scopedSubcontracts = useMemo(() => {
    return (subContracts || []).filter((sc: any) => sc.projectId === projectId);
  }, [subContracts, projectId]);

  const activeTransferProjects = useMemo(() => {
    return (projects || [])
      .filter((p: any) =>
        p.id !== projectId &&
        String(p.status || "Active").toLowerCase() === "active" &&
        !p.archived
      )
      .sort((a: any, b: any) => String(a.name || "").localeCompare(String(b.name || "")));
  }, [projects, projectId]);

  const transferSourceProjectId = transferDirection === "out" ? projectId : transferProjectId;
  const editingTransfer = editingTransferId
    ? transfers.find((transfer) => transfer.id === editingTransferId)
    : undefined;
  const editingAmountToRestore = editingTransfer
    && editingTransfer.source_project_id === transferSourceProjectId
    && editingTransfer.payment_method === transferMethod
      ? Number(editingTransfer.amount) || 0
      : 0;
  const availableToTransfer = (transferSourceProjectId
    ? Number(projectBalances[transferSourceProjectId]?.[transferMethod] || 0)
    : 0) + editingAmountToRestore;
  const requestedTransferAmount = Number(transferAmount) || 0;
  const transferInsufficient = requestedTransferAmount > availableToTransfer + 0.0001;

  const handleTransfer = async () => {
    if (!transferProjectId || !transferDate || requestedTransferAmount <= 0) {
      alert("Other project, amount, method, and date are required.");
      return;
    }
    if (transferInsufficient) {
      alert(`Insufficient ${transferMethod} funds. Available: ${money(Math.max(0, availableToTransfer))}.`);
      return;
    }

    const sourceProjectId = transferDirection === "out" ? projectId : transferProjectId;
    const destinationProjectId = transferDirection === "out" ? transferProjectId : projectId;
    setTransferSaving(true);
    try {
      await apiRequest(editingTransferId ? "PATCH" : "POST", editingTransferId
        ? `/api/project_transfers/${encodeURIComponent(editingTransferId)}`
        : "/api/project_transfers", {
        tenantId,
        body: {
          ...(editingTransferId ? {} : { id: generateId("TRF") }),
          source_project_id: sourceProjectId,
          destination_project_id: destinationProjectId,
          amount: requestedTransferAmount,
          payment_method: transferMethod,
          date: transferDate,
          notes: transferNotes.trim() || null,
        },
      });
      setTransferProjectId("");
      setTransferAmount("");
      setTransferNotes("");
      setEditingTransferId(null);
      await load();
      await onChanged?.();
    } catch (error: any) {
      alert(error?.message || "Failed to create project transfer.");
    } finally {
      setTransferSaving(false);
    }
  };

  const startTransferEdit = (transfer: ApiTransfer) => {
    const outgoing = transfer.source_project_id === projectId;
    setEditingTransferId(transfer.id);
    setTransferDirection(outgoing ? "out" : "in");
    setTransferProjectId(outgoing ? transfer.destination_project_id : transfer.source_project_id);
    setTransferAmount(String(transfer.amount));
    setTransferMethod(transfer.payment_method);
    setTransferDate(transfer.date);
    setTransferNotes(transfer.notes || "");
  };

  const cancelTransferEdit = () => {
    setEditingTransferId(null);
    setTransferProjectId("");
    setTransferAmount("");
    setTransferNotes("");
  };

  const deleteTransfer = async (transfer: ApiTransfer) => {
    const otherProject = transfer.source_project_id === projectId
      ? transfer.destination_project_name
      : transfer.source_project_name;
    if (!window.confirm(`Delete this project transfer?\n\n${money(transfer.amount)} • ${transfer.payment_method}\n${otherProject || ""}`)) return;
    try {
      await apiRequest("DELETE", `/api/project_transfers/${encodeURIComponent(transfer.id)}`, { tenantId });
      await load();
      await onChanged?.();
    } catch (error: any) {
      alert(error?.message || "Failed to delete project transfer.");
    }
  };

  return (
    <div className="container" style={{ padding: 0 }}>
      <style>{`
        .expGrid {
          display: grid;
          gap: 12px;
          align-items: end;
          grid-template-columns:
            minmax(200px, 1.1fr)
            minmax(180px, 0.8fr)
            minmax(180px, 0.8fr)
            minmax(200px, 1fr)
            minmax(260px, 1.2fr);
        }
        .expCell { display:flex; flex-direction:column; min-width:0; }

        @media (max-width: 1180px) { .expGrid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 680px) { .expGrid { grid-template-columns: 1fr; } }

        .fieldLabel { color: var(--muted); font-weight: 900; font-size: 12px; margin-bottom: 6px; }
        .fieldInput {
          width: 100%;
          min-width: 0;
          max-width: 100%;
          padding: 10px 12px;
          border-radius: 12px;
          border: 1px solid rgba(15,23,42,0.12);
          background: rgba(255,255,255,0.85);
          outline: none;
          box-sizing: border-box;
        }
        .fieldInput:focus { border-color: rgba(15,23,42,0.22); box-shadow: 0 0 0 3px rgba(15,23,42,0.08); }
        .pillBtn { border-radius: 999px; padding: 8px 12px; font-weight: 900; line-height: 1; white-space: nowrap; }

        .totalsRow {
          display:grid;
          gap: 10px;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          margin-top: 12px;
        }
        @media (max-width: 980px) { .totalsRow { grid-template-columns: 1fr; } }
        .miniRow {
          display:flex; justify-content:space-between; align-items:baseline;
          padding: 8px 10px; border-radius: 14px;
          border: 1px solid rgba(15,23,42,0.08);
          background: rgba(255,255,255,0.70);
        }
        .miniLabel { color: var(--muted); font-weight: 900; font-size: 12px; }
        .miniValue { font-weight: 1000; letter-spacing: -0.2px; }
        .numRight { text-align:right; }
        .rowActions{ display:flex; gap:8px; justify-content:flex-end; flex-wrap:wrap; }

        .whoPaidGrid {
          display:grid;
          grid-template-columns: 1fr;
          gap: 8px;
        }
        .whoPaidHint {
          font-size: 12px;
          color: var(--muted);
          font-weight: 800;
        }

        .typeDetailLine {
          margin-top: 6px;
          font-size: 12px;
          color: var(--muted);
          font-weight: 800;
        }
        .transferGrid {
          display:grid;
          gap:12px;
          align-items:end;
          grid-template-columns: minmax(170px,.8fr) minmax(240px,1.4fr) minmax(140px,.7fr) minmax(130px,.65fr) minmax(160px,.8fr);
        }
        @media (max-width: 980px) { .transferGrid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 680px) { .transferGrid { grid-template-columns: 1fr; } }
        .transferWarning {
          margin-top:10px; padding:10px 12px; border-radius:12px;
          background:rgba(245,158,11,.12); color:#a15c00; font-weight:900;
        }
      `}</style>

      <Card
        title={
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div style={{ fontWeight: 1000, letterSpacing: "-0.2px" }}>{t("expenses.title")}</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <div className="badge" title={t("expenses.tab.totalTitle")}>
                {t("common.total")}: <span style={{ fontWeight: 1000 }}>{money(totals.total)}</span>
              </div>
              <button className="btn btnSm pillBtn" onClick={handleAdd} type="button">
                {t("expenses.addBtn")}
              </button>
            </div>
          </div>
        }
        subtitle={t("expenses.tab.subtitle")}
      >
        {err ? (
          <div style={{ color: "var(--bad)", fontWeight: 800, marginBottom: 10 }}>{err}</div>
        ) : null}

        <div className="expGrid">
          <div className="expCell">
            <div className="fieldLabel">{t("common.type")}</div>
            <select
              className="fieldInput"
              value={expenseType}
              onChange={(e) => {
                const v = e.target.value as ExpenseType;
                setExpenseType(v);
                if (v !== "Sub-Contract Payment") setSubContractId("");
              }}
            >
              <option value="Expense">{t("expenses.expenseBadge")}</option>
              <option value="Sub-Contract Payment">{t("expenses.subPaymentBadge")}</option>
            </select>
          </div>

          <div className="expCell">
            <div className="fieldLabel">{t("expenses.whoPaidLabel")}</div>
            <div className="whoPaidGrid">
              <select
                className="fieldInput"
                value={whoPaidMode === "custom" ? "__CUSTOM__" : whoPaid}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "__CUSTOM__") {
                    setWhoPaidMode("custom");
                    setWhoPaidCustom("");
                    setWhoPaid("Company");
                  } else {
                    setWhoPaidMode("select");
                    setWhoPaidCustom("");
                    setWhoPaid(v);
                  }
                }}
              >
                {payerOptions.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
                <option value="__CUSTOM__">Custom…</option>
              </select>

              {whoPaidMode === "custom" ? (
                <input
                  className="fieldInput"
                  value={whoPaidCustom}
                  onChange={(e) => setWhoPaidCustom(e.target.value)}
                  placeholder={t("expenses.tab.payerPlaceholder")}
                />
              ) : (
                <div className="whoPaidHint">Tip: Select custom if name not listed</div>
              )}
            </div>
          </div>

          <div className="expCell">
            <div className="fieldLabel">{t("common.amount")}</div>
            <input
              className="fieldInput"
              type="number"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
            />
          </div>

          <div className="expCell">
            <div className="fieldLabel">{t("common.method")}</div>
            <select
              className="fieldInput"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
            >
              <option value="Cash">{t("common.cash")}</option>
              <option value="Bank">{t("common.bank")}</option>
            </select>
          </div>

          <div className="expCell">
            <div className="fieldLabel">{t("common.date")}</div>
            <input
              className="fieldInput"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          <div className="expCell" style={{ gridColumn: "1 / -1" }}>
            <div className="fieldLabel">{t("common.notes")}</div>
            <input
              className="fieldInput"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("expenses.tab.notesPlaceholder")}
            />
          </div>

          {expenseType === "Sub-Contract Payment" ? (
            <div className="expCell" style={{ gridColumn: "1 / -1" }}>
              <div className="fieldLabel">{t("expenses.subContractLabel")}</div>
              <select
                className="fieldInput"
                value={subContractId}
                onChange={(e) => setSubContractId(e.target.value)}
              >
                <option value="">{t("expenses.selectSubContract")}</option>
                {scopedSubcontracts.map((sc: any) => (
                  <option key={sc.id} value={sc.id}>
                    {subContractPretty(sc.id)}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </div>

        <div className="totalsRow">
          <div className="miniRow">
            <div className="miniLabel">{t("expenses.tab.cashExpenses")}</div>
            <div className="miniValue numBad">{money(totals.cash)}</div>
          </div>
          <div className="miniRow">
            <div className="miniLabel">{t("expenses.tab.bankExpenses")}</div>
            <div className="miniValue numBad">{money(totals.bank)}</div>
          </div>
          <div className="miniRow">
            <div className="miniLabel">{t("expenses.tab.subcontractPayments")}</div>
            <div className="miniValue numBad">{money(totals.subcontractPayments)}</div>
          </div>
          <div className="miniRow">
            <div className="miniLabel">{t("expenses.tab.totalExpenses")}</div>
            <div className="miniValue numBad">{money(totals.total)}</div>
          </div>
        </div>
      </Card>

      <div style={{ marginTop: 14 }}>
        <Card
          title="Transfer Funds"
          subtitle="Move project funds without changing revenue, expenses, or profit. Cash stays Cash and Bank stays Bank."
          right={
            <button
              className="btn btnSm pillBtn"
              type="button"
              onClick={handleTransfer}
              disabled={transferSaving || transferInsufficient || !transferProjectId || requestedTransferAmount <= 0}
            >
              {transferSaving ? "Saving…" : editingTransferId ? "Update Transfer" : "Transfer"}
            </button>
          }
        >
          <div className="transferGrid">
            <div className="expCell">
              <div className="fieldLabel">Direction</div>
              <select className="fieldInput" value={transferDirection} onChange={(e) => {
                setTransferDirection(e.target.value as TransferDirection);
                setTransferProjectId("");
              }}>
                <option value="out">Send to project</option>
                <option value="in">Receive from project</option>
              </select>
            </div>
            <div className="expCell">
              <div className="fieldLabel">Other active project</div>
              <select className="fieldInput" value={transferProjectId} onChange={(e) => setTransferProjectId(e.target.value)}>
                <option value="">Select active project</option>
                {activeTransferProjects.map((p: any) => (
                  <option key={p.id} value={p.id}>
                    {projectDisplayLabelPretty(p, clients || [])}
                  </option>
                ))}
              </select>
            </div>
            <div className="expCell">
              <div className="fieldLabel">Amount</div>
              <input className="fieldInput" type="number" inputMode="decimal" min="0" step="0.01" value={transferAmount} onChange={(e) => setTransferAmount(e.target.value)} placeholder="0.00" />
            </div>
            <div className="expCell">
              <div className="fieldLabel">Method</div>
              <select className="fieldInput" value={transferMethod} onChange={(e) => setTransferMethod(e.target.value as PaymentMethod)}>
                <option value="Cash">Cash</option>
                <option value="Bank">Bank</option>
              </select>
            </div>
            <div className="expCell">
              <div className="fieldLabel">Date</div>
              <input className="fieldInput" type="date" value={transferDate} onChange={(e) => setTransferDate(e.target.value)} />
            </div>
            <div className="expCell" style={{ gridColumn: "1 / -1" }}>
              <div className="fieldLabel">Notes</div>
              <input className="fieldInput" value={transferNotes} onChange={(e) => setTransferNotes(e.target.value)} placeholder="Reason or accounting reference" />
            </div>
          </div>

          <div style={{ marginTop: 10, color: "var(--muted)", fontWeight: 800 }}>
            Source {transferMethod} available: <b>{money(Math.max(0, availableToTransfer))}</b>
          </div>
          {transferInsufficient ? (
            <div className="transferWarning">
              Insufficient {transferMethod} funds. Reduce the amount or select another source project.
            </div>
          ) : null}
          {editingTransferId ? (
            <button className="btn btnSecondary btnSm" type="button" onClick={cancelTransferEdit} style={{ marginTop: 10 }}>
              Cancel Edit
            </button>
          ) : null}
        </Card>
      </div>

      <div style={{ marginTop: 14 }}>
        <TableShell
          title="Project Transfers"
          subtitle={transfers.length ? `${transfers.length} linked transfer${transfers.length === 1 ? "" : "s"}` : "No project transfers yet"}
        >
          {transfers.length === 0 ? (
            <div style={{ color: "var(--muted)", fontStyle: "italic", padding: "10px 2px" }}>
              Transfers sent or received by this project will appear here.
            </div>
          ) : (
            <table>
              <thead><tr>
                <th style={{ width: 130 }}>Date</th><th style={{ width: 130 }}>Direction</th><th>Other project</th>
                <th style={{ width: 120 }}>Method</th><th className="numRight" style={{ width: 150 }}>Amount</th>
                <th>Notes</th><th style={{ width: 190 }} />
              </tr></thead>
              <tbody>
                {transfers.map((transfer) => {
                  const outgoing = transfer.source_project_id === projectId;
                  const otherProjectId = outgoing
                    ? transfer.destination_project_id
                    : transfer.source_project_id;
                  const otherProject = (projects || []).find((p: any) => p.id === otherProjectId);
                  const otherProjectLabel = otherProject
                    ? projectDisplayLabelPretty(otherProject, clients || [])
                    : outgoing
                    ? transfer.destination_project_name || otherProjectId
                    : transfer.source_project_name || otherProjectId;
                  return <tr key={transfer.id}>
                    <td style={{ whiteSpace: "nowrap" }}>{transfer.date}</td>
                    <td><span className={`badge ${outgoing ? "" : "badgeGood"}`}>{outgoing ? "Transfer Out" : "Transfer In"}</span></td>
                    <td style={{ fontWeight: 900 }}>
                      <CellClamp lines={2} title={otherProjectLabel}>{otherProjectLabel}</CellClamp>
                    </td>
                    <td><span className={`badge ${transfer.payment_method === "Bank" ? "badgeGood" : ""}`}>{transfer.payment_method}</span></td>
                    <td className="numRight" style={{ whiteSpace: "nowrap", fontWeight: 1000, color: outgoing ? "var(--bad)" : "var(--good)" }}>
                      {outgoing ? "−" : "+"}{money(transfer.amount)}
                    </td>
                    <td><CellClamp lines={2} title={transfer.notes || ""}>{transfer.notes || <span style={{ color: "var(--muted)" }}>—</span>}</CellClamp></td>
                    <td><div className="rowActions">
                      <button className="btn btnSecondary btnSm" type="button" onClick={() => startTransferEdit(transfer)}>Edit</button>
                      <button className="btn btnSecondary btnSm" type="button" style={{ color: "#b00020" }} onClick={() => deleteTransfer(transfer)}>Delete</button>
                    </div></td>
                  </tr>;
                })}
              </tbody>
            </table>
          )}
        </TableShell>
      </div>

      <div style={{ marginTop: 14 }}>
        <TableShell
          title={t("expenses.ledgerTitle")}
          subtitle={
            loading
              ? t("expenses.tab.loadingMsg")
              : items.length
              ? t("expenses.tab.expenseCount", { count: items.length })
              : t("expenses.tab.noExpensesYet")
          }
          right={
            <span className="badge">
              {t("common.total")}: <b>{money(totals.total)}</b>
            </span>
          }
        >
          {items.length === 0 ? (
            <div style={{ color: "var(--muted)", fontStyle: "italic", padding: "10px 2px" }}>
              {t("expenses.tab.empty")}
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th style={{ width: 155 }}>{t("common.date")}</th>

                  {/* ✅ give Type + Notes more breathing room */}
                  <th style={{ width: 320 }}>{t("common.type")}</th>

                  <th style={{ width: 170 }}>{t("expenses.whoPaidLabel")}</th>
                  <th style={{ width: 120 }}>{t("common.method")}</th>

                  <th style={{ width: 150 }} className="numRight">
                    {t("common.amount")}
                  </th>

                  <th style={{ width: 360 }}>{t("common.notes")}</th>

                  <th style={{ width: 200 }} />
                </tr>
              </thead>

              <tbody>
                {items.map((e) => {
                  const isEditing = editingId === e.id;

                  return (
                    <tr key={e.id}>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {isEditing ? (
                          <input
                            className="fieldInput"
                            type="date"
                            value={editDate}
                            onChange={(x) => setEditDate(x.target.value)}
                          />
                        ) : (
                          e.date
                        )}
                      </td>

                      <td>
                        {isEditing ? (
                          <select
                            className="fieldInput"
                            value={editExpenseType}
                            onChange={(x) => {
                              const v = x.target.value as ExpenseType;
                              setEditExpenseType(v);
                              if (v !== "Sub-Contract Payment") setEditSubContractId("");
                            }}
                          >
                            <option value="Expense">{t("expenses.expenseBadge")}</option>
                            <option value="Sub-Contract Payment">{t("expenses.subPaymentBadge")}</option>
                          </select>
                        ) : (
                          <span className="badge">{e.expenseType}</span>
                        )}

                        {/* ✅ show pretty subcontract under Type for subcontract payments */}
                        {!isEditing && e.expenseType === "Sub-Contract Payment" && e.subContractId ? (
                          <CellClamp
                            lines={2}
                            title={subContractPretty(String(e.subContractId))}
                          >
                            <div className="typeDetailLine">
                              {subContractPretty(String(e.subContractId))}
                            </div>
                          </CellClamp>
                        ) : null}

                        {isEditing && editExpenseType === "Sub-Contract Payment" ? (
                          <div style={{ marginTop: 8 }}>
                            <select
                              className="fieldInput"
                              value={editSubContractId}
                              onChange={(x) => setEditSubContractId(x.target.value)}
                            >
                              <option value="">{t("expenses.selectSubContract")}</option>
                              {scopedSubcontracts.map((sc: any) => (
                                <option key={sc.id} value={sc.id}>
                                  {subContractPretty(sc.id)}
                                </option>
                              ))}
                            </select>
                          </div>
                        ) : null}
                      </td>

                      <td>
                        {isEditing ? (
                          <div className="whoPaidGrid">
                            <select
                              className="fieldInput"
                              value={editWhoPaidMode === "custom" ? "__CUSTOM__" : editWhoPaid}
                              onChange={(ev) => {
                                const v = ev.target.value;
                                if (v === "__CUSTOM__") {
                                  setEditWhoPaidMode("custom");
                                  setEditWhoPaidCustom("");
                                  setEditWhoPaid("Company");
                                } else {
                                  setEditWhoPaidMode("select");
                                  setEditWhoPaidCustom("");
                                  setEditWhoPaid(v);
                                }
                              }}
                            >
                              {payerOptions.map((p) => (
                                <option key={p} value={p}>
                                  {p}
                                </option>
                              ))}
                              <option value="__CUSTOM__">Custom…</option>
                            </select>

                            {editWhoPaidMode === "custom" ? (
                              <input
                                className="fieldInput"
                                value={editWhoPaidCustom}
                                onChange={(x) => setEditWhoPaidCustom(x.target.value)}
                                placeholder={t("expenses.tab.payerPlaceholder")}
                              />
                            ) : null}
                          </div>
                        ) : (
                          <CellClamp lines={2} title={e.whoPaid || ""}>
                            <span style={{ fontWeight: 900 }}>{e.whoPaid}</span>
                          </CellClamp>
                        )}
                      </td>

                      <td>
                        {isEditing ? (
                          <select
                            className="fieldInput"
                            value={editPaymentMethod}
                            onChange={(x) => setEditPaymentMethod(x.target.value as PaymentMethod)}
                          >
                            <option value="Cash">{t("common.cash")}</option>
                            <option value="Bank">{t("common.bank")}</option>
                          </select>
                        ) : (
                          <span className={`badge ${e.paymentMethod === "Cash" ? "" : "badgeGood"}`}>
                            {e.paymentMethod === "Cash" ? t("common.cash") : t("common.bank")}
                          </span>
                        )}
                      </td>

                      <td className="numRight" style={{ whiteSpace: "nowrap" }}>
                        {isEditing ? (
                          <input
                            className="fieldInput"
                            type="number"
                            inputMode="decimal"
                            value={editAmount}
                            onChange={(x) => setEditAmount(x.target.value)}
                          />
                        ) : (
                          <span style={{ fontWeight: 1000 }}>{money(e.amount)}</span>
                        )}
                      </td>

                      <td>
                        {isEditing ? (
                          <input
                            className="fieldInput"
                            value={editNotes}
                            onChange={(x) => setEditNotes(x.target.value)}
                            placeholder={t("expenses.optionalNotes")}
                          />
                        ) : (
                          <CellClamp lines={2} title={e.notes || ""}>
                            {e.notes || <span style={{ color: "var(--muted)" }}>—</span>}
                          </CellClamp>
                        )}
                      </td>

                      <td>
                        {isEditing ? (
                          <div className="rowActions">
                            <button className="btn btnSm" onClick={() => saveEdit(e)} type="button">
                              {t("common.save")}
                            </button>
                            <button
                              className="btn btnSecondary btnSm"
                              onClick={cancelEdit}
                              type="button"
                            >
                              {t("common.cancel")}
                            </button>
                          </div>
                        ) : (
                          <div className="rowActions">
                            <button
                              className="btn btnSecondary btnSm"
                              onClick={() => startEdit(e)}
                              type="button"
                            >
                              {t("common.edit")}
                            </button>
                            <button
                              className="btn btnSecondary btnSm"
                              onClick={() => doDelete(e)}
                              type="button"
                              style={{ color: "#b00020" }}
                            >
                              {t("common.delete")}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}

                <tr>
                  <td colSpan={4} style={{ fontWeight: 1000, textAlign: "right" }}>
                    {t("expenses.tab.totalExpenses")}
                  </td>
                  <td className="numRight" style={{ fontWeight: 1000, whiteSpace: "nowrap" }}>
                    {money(totals.total)}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tbody>
            </table>
          )}
        </TableShell>
      </div>
    </div>
  );
}
